import {
  EventType,
  InteractionType,
  IPublicClientApplication,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { b2cPolicies } from '../config/AuthConfig.base';
import { performLogout } from '../lib/LogoutUtility';
type MsalEventCallbackParams = {
  eventType: EventType;
  error: {
    errorMessage: string | string[];
  };
  interactionType: InteractionType;
  payload: {
    idTokenClaims: {
      [x: string]: string;
    };
  };
};

export function useConfigureAuth() {
  const { instance } = useMsal();

  /**
   * Using the event API, you can register an event callback that will do something when an event is emitted.
   * When registering an event callback in a react component you will need to make sure you do 2 things.
   * 1) The callback is registered only once
   * 2) The callback is unregistered before the component unmounts.
   * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/events.md
   */
  useEffect(() => {
    const callbackId = instance.addEventCallback(
      (event: MsalEventCallbackParams) => {
        handleLoginFailureEvent(event, instance);
        handleLoginSuccess(event, instance);
      }
    );

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  });
}

function handleLoginSuccess(
  event: MsalEventCallbackParams,
  instance: IPublicClientApplication
): void {
  if (event.eventType === EventType.LOGIN_SUCCESS) {
    if (event?.payload) {
      /**
       * We need to reject id tokens that were not issued with the default sign-in policy.
       * "acr" claim in the token tells us what policy is used (NOTE: for new policies (v2.0), use "tfp" instead of "acr").
       * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
       */
      const policyUsed =
        event.payload.idTokenClaims['tfp'] ||
        event.payload.idTokenClaims['acr'];

      if (policyUsed === b2cPolicies.names.forgotPassword) {
        window.alert(
          'Password has been reset successfully. \nPlease sign-in with your new password'
        );
        performLogout(instance);
      }
    }
  }
}

function handleLoginFailureEvent(
  event: MsalEventCallbackParams,
  instance: IPublicClientApplication
) {
  if (event.eventType === EventType.LOGIN_FAILURE) {
    if (event.error && event.error.errorMessage.indexOf('AADB2C90118') > -1) {
      if (event.interactionType === InteractionType.Redirect) {
        instance.loginRedirect(
          b2cPolicies.authorities.forgotPassword as RedirectRequest
        );
      } else if (event.interactionType === InteractionType.Popup) {
        instance
          .loginPopup(b2cPolicies.authorities.forgotPassword as PopupRequest)
          .catch((e) => {
            return;
          });
      }
    }
  }
}
